import {
    UiComponentDataContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import {ReactElement} from "react";
import {TextField as TextFieldComponent} from "../model/TextField";
import TextField from "../../../../../../components/fields/textfield/TextField";
import {AttributeDefinitionDto} from "../../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {ElementDto} from "../../../../../../common/apis/element/ElementDto";
import {UiComponent} from "../model/UiComponent";
import {t} from "../../../../../../store/localization/TranslMessasge";
import elementService from "../../../elementdetail/service/ElementService";
import {PropertyValuePersister} from "./PropertyValuePersister";
import {ValuePersister} from "./ValuePersister";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";

export class TextFieldPlugin implements UiComponentRendererPlugin {

    private readonly valuePersister: ValuePersister = new PropertyValuePersister(elementService);

    getSupportedComponentTypes(): string[] {
        return ["TextField"];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const attributeDefinitionId = component.attributeDefinitionId;
        const value = this.getPropertyValue(dataContext.element, dataContext.attributeDefinitions[component.attributeDefinitionId]);
        if (value) {
            return Promise.resolve({
                [attributeDefinitionId]: value
            });
        } else {
            return Promise.resolve({});
        }
    }

    private getPropertyValue(element: ElementDto, attributeDefinition?: AttributeDefinitionDto): string | undefined {
        const property = element.properties?.find(prop => prop.definition.name === attributeDefinition?.name);
        if (property) {
            return property.value;
        } else {
            return undefined;
        }
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const textField = component as TextFieldComponent;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[textField.attributeDefinitionId];
        if (!attributeDefinition) {
            return {};
        }

        const value = stateContext.values[textField.attributeDefinitionId];
        return validationContext.valueValidator.validateValue(value, attributeDefinition, t);
    }

    renderComponent({component, dataContext, stateContext, renderingContext}: UiComponentRenderingProps): ReactElement {
        const textField = component as TextFieldComponent;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[textField.attributeDefinitionId];
        if (!attributeDefinition) {
            return (
                    <ErrorFieldPlaceholder key={textField.id} error={t(StereotypesTranslationKey.UNKNOWN_ATTRIBUTE)} />
            );
        }

        const value = stateContext.values[textField.attributeDefinitionId];
        const error = stateContext.errors[textField.attributeDefinitionId];
        const readOnly = textField.readOnly || !stateContext.editMode;

        return (
                <TextField
                        key={textField.id}
                        label={attributeDefinition.name}
                        value={value ?? ""}
                        onChange={(value) => stateContext.onValuesChange({[textField.attributeDefinitionId]: value})}
                        placeholder={textField.placeholder}
                        helperText={textField.helperText}
                        errorMessage={error ? t(error) : undefined}
                        required={attributeDefinition.required}
                        readOnly={readOnly}
                />
        );
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext): Promise<void>[] {
        const textField = component as TextFieldComponent;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[textField.attributeDefinitionId];
        if (!attributeDefinition) {
            return [];
        }

        const value = stateContext.values[textField.attributeDefinitionId];
        return [this.valuePersister.updateValue(dataContext.element, attributeDefinition, value)];
    }

}